import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'reactstrap';
import { checkPrompt, tidyPrompt } from '../../helper/backend-methods';
import { showToast } from '../../components/Toast';
import { assemblePrompt, emptySections, EXTRA } from './sections';
import '../../assets/css/--component-rubric.css';

/**
 * What a prompt is missing, beside the prompt.
 *
 * Shown to every customer on their own prompt, decided 23 Oct 2026. It is a
 * list of what is missing, never a score out of 100 — a number invites gaming,
 * and a list can be acted on.
 *
 * The check costs nothing and returns in milliseconds, so it can run while
 * someone types. It is still debounced: free on the server is not free over a
 * network, and a request per keystroke would arrive out of order.
 *
 * The rules live in the backend, not here. A second copy in the browser would
 * drift, and the drift would read as the panel disagreeing with the save.
 *
 * One thing it will not do is list five missing sections at someone whose
 * prompt is plain prose. Scoring production on 27 Oct 2026 found 110 of 140
 * customer prompts in that state — they say who the agent is and what it must
 * never do, they just do not say it under headings. Telling those customers
 * they are missing everything would be both wrong and useless, so prose gets
 * one row and a button that fixes it instead.
 */

const EMPTY = { sections: [], warnings: [], complete: false };

const RubricPanel = ({ prompt, firstMessage, promptId, hasTools, onScore, onTidied, canTidy }) => {
  const [score, setScore] = useState(null);
  const [checking, setChecking] = useState(false);
  const [failed, setFailed] = useState(false);
  const [tidying, setTidying] = useState(false);
  // Only the newest answer counts: a slow reply from two keystrokes ago must
  // not overwrite a fresh one.
  const latest = useRef(0);

  const body = useMemo(
    () => (promptId && !prompt ? { promptId } : { prompt: prompt || '', firstMessage: firstMessage || '', hasTools: !!hasTools }),
    [prompt, firstMessage, promptId, hasTools],
  );

  useEffect(() => {
    if (!body.promptId && !body.prompt?.trim()) {
      setScore(null);
      return undefined;
    }

    const mine = ++latest.current;
    setChecking(true);
    const timer = setTimeout(async () => {
      try {
        const response = await checkPrompt(body);
        if (mine !== latest.current) return;
        if (response?.status) {
          const result = response.data?.data ?? response.data ?? EMPTY;
          setScore(result);
          setFailed(false);
          if (onScore) onScore(result);
        } else {
          setFailed(true);
        }
      } catch {
        if (mine === latest.current) setFailed(true);
      } finally {
        if (mine === latest.current) setChecking(false);
      }
    }, 600);

    return () => clearTimeout(timer);
    // onScore is a callback the parent may redefine each render; depending on
    // it would re-check on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body.prompt, body.firstMessage, body.promptId, body.hasTools]);

  // Nothing typed yet. No empty state, no illustration — a blank box is a
  // normal thing to meet before you have written anything.
  if (!score && !checking && !failed) return null;

  // The check failing is our problem, not the customer's, and it says nothing
  // about their prompt. Silence beats a red box they cannot act on.
  if (failed && !score) return null;

  // Prose, not sections. The backend distinguishes these: `structured` is
  // false only when the prompt has no headings at all, which is a different
  // problem from a prompt that has five of six.
  const asProse = score && score.structured === false && (prompt || '').trim();
  const offerTidy = asProse && canTidy && onTidied;

  const missing = asProse ? [] : (score?.sections ?? []).filter((s) => s.required && !s.present);
  const warnings = score?.warnings ?? [];
  const nothingWrong = score && !asProse && !missing.length && !warnings.length;

  /**
   * Sort the prose into sections, without rewording any of it.
   *
   * This only changes what is on screen. Saving is still the customer's to
   * press, and the version it would replace is kept either way.
   */
  const sortIntoSections = async () => {
    setTidying(true);
    try {
      const response = await tidyPrompt(prompt);
      const data = response?.data?.data ?? response?.data;
      if (!response?.status || !data?.sections) {
        showToast('error', 'Could not sort that', data?.message || 'Your prompt is unchanged. Please try again.');
        return;
      }
      // `leftover` is whatever the model could not place. It is still the
      // customer's text, so it is carried through rather than dropped.
      onTidied(
        assemblePrompt({ ...emptySections(), ...data.sections, [EXTRA]: (data.leftover || '').trim() }),
      );
      const lost = data.droppedSentences || [];
      if (lost.length) {
        showToast('error', `${lost.length} line(s) did not fit`, `Check it over — this landed nowhere: \u201c${lost[0].slice(0, 70)}\u201d`);
      } else {
        showToast('success', 'Sorted into sections', 'Nothing was reworded. Read it over, then save.');
      }
    } catch {
      showToast('error', 'Could not sort that', 'Your prompt is unchanged. Please try again.');
    } finally {
      setTidying(false);
    }
  };

  return (
    <div className="rubric-panel" aria-live="polite">
      <div className="rubric-panel-head">
        <span>What this prompt is missing</span>
        {checking && <span className="rubric-panel-checking">checking…</span>}
      </div>

      {nothingWrong && (
        <div className="rubric-row rubric-row-ok">
          <i className="fa fa-check-circle" aria-hidden="true" />
          <span>This prompt has everything a voice agent needs.</span>
        </div>
      )}

      {asProse && (
        <div className="rubric-row rubric-row-advice">
          <i className="fa fa-info-circle" aria-hidden="true" />
          <div>
            <span>
              This prompt is written as plain text. It may well say everything it needs to —
              but models weight the headings, so sections get followed more reliably.
            </span>
            {offerTidy && (
              <div className="rubric-tidy">
                <Button color="primary" size="sm" disabled={tidying} onClick={sortIntoSections}>
                  {tidying ? 'Sorting\u2026' : 'Sort it into the boxes'}
                </Button>
                <small>Nothing is reworded. You still choose whether to save.</small>
              </div>
            )}
          </div>
        </div>
      )}

      {missing.map((section) => (
        <div className="rubric-row rubric-row-missing" key={section.heading}>
          <i className="fa fa-exclamation-circle" aria-hidden="true" />
          <span>{section.note || `Nothing in “${section.label}” yet`}</span>
        </div>
      ))}

      {warnings.map((warning, index) => (
        <div
          className={`rubric-row ${warning.level === 'missing' ? 'rubric-row-missing' : 'rubric-row-advice'}`}
          key={`${warning.level}-${index}`}
        >
          <i className="fa fa-exclamation-circle" aria-hidden="true" />
          <span>{warning.text}</span>
        </div>
      ))}

      <p className="rubric-panel-foot">
        Checked as you type. It costs nothing and never makes you wait.
      </p>
    </div>
  );
};

export default RubricPanel;
